import { find } from 'lodash';
import { getAllCountrySettings, getGeoLocationInfo } from '../commands/internationalCommands';
import { preferredLanguage } from '../data-types/cookies/cookiesConstants';
import { auth, HELP_BETKWIFF_EMAIL, HELP_KWIFF_EMAIL } from '../data-types/initialisation/initialisationConstants';
import {
  acceptedCountriesList,
  americanOddsWhiteListedCountries,
  availableLanguages,
  BETKWIFF_BRAND_NAME,
  BETKWIFF_DOMAIN,
  bypassReCaptchaWhiteListCountries,
  cancelWithdrawalBlockedCountries,
  COUNTRIES_WITH_AVIATOR_GAME_IN_MENU,
  COUNTRIES_WITH_FRONTEND_DISABLED_CASINO,
  COUNTRIES_WITHOUT_CLOSED_LOOP_WITHDRAWALS,
  countriesWithBetTaxes,
  countriesWithCallUs,
  countriesWithHiddenFooterForGhostUsers,
  countriesWithHiddenPaymentLogosInFooter,
  countriesWithoutCorporateLinksInFooter,
  CountrySpecificLanguage,
  depositAcknowledgeDialogDisplayForCountries,
  displayHROnFeaturedPageCountries,
  forceBlockSystemBetsForCountries,
  hideAccountDetailsForCountries,
  hideCloseAccountForCountries,
  hideRAFButtonFromSurpriseBetCountries,
  hideResponsibleGamblingForCountries,
  hideShareButtonFromSurpriseBetCountries,
  hideStayingInControlForCountries,
  hideVerificationForCountries,
  KWIFF_BRAND_NAME,
  KWIFF_DOMAIN,
  PROJECT_PHOENIX_BRAND_NAME,
  promotionTabsWhiteListedCountries,
  referAFriendEligibleCountries,
  signupMandatoryFirstDepositCountriesList,
  termsAndConditionsAutoCheckedForCountries,
  walletProfitAndLossBlockedCountries,
} from '../data-types/international/internationalConstants';
import {
  AcceptedCountries,
  AvailableLanguage,
  CUSTOMER_SERVICE_EMAIL,
  LiveMatchTrackerLanguage,
} from '../data-types/international/internationalEnums';
import { fourCharactersRegex, passwordRegex } from '../data-types/regex/regexConstants';
import { PASSWORD_PATTERN } from '../data-types/signup/signupEnums';
import {
  AVIATOR_ALLOWED_IN_MENU,
  COMBO_BETS_ENABLED,
  KWIFF_DOMAINS,
  PROJECT_PHOENIX_DOMAIN_ORIGIN,
  ZENDESK_DISABLE_IN_COUNTRIES,
} from '../initialisation/initialisation';
import { PROD_KENYA_DOMAIN } from '../routing-system/appPaths';
import {
  setAllAvailableCountrySettings,
  setBetkwiffDomainFeatureFlag,
  setCanPlayCasino,
  setCanShowAviator,
} from '../state/actions/customizationActions';
import { setLoginOptions } from '../state/actions/loginActions';
import { setSignupChoiceDialogOpen, setUseSignupV3 } from '../state/actions/signupActions';
import { setGeoLocalisation, setLocale, setUserLocation } from '../state/actions/userDataActions';
import { store } from '../state/store';
import { isDevBetkwiff, isDevEnv, isDevKwiff, isLocalhost, isProductionEnv } from '../utils/general';
import { getCookie } from '../utils/incomeAccess';
import { handleJoin } from '../utils/signup';
import { setCountrySettingsByUserLocation } from '../utils/user';
import { tryToGetWalletSettings } from '../utils/wallet';
import deJson from './i18/de.json';
import enJson from './i18/en.json';
import en_CAJson from './i18/en_CA.json';
import en_GBJson from './i18/en_GB.json';
import en_IEJson from './i18/en_IE.json';
import en_INJson from './i18/en_IN.json';
import en_KEJson from './i18/en_KE.json';
import en_NZJson from './i18/en_NZ.json';
import esJson from './i18/es.json';
import fiJson from './i18/fi.json';
import frJson from './i18/fr.json';
import hiJson from './i18/hi.json';
import huJson from './i18/hu.json';
import pt_BRJson from './i18/pt_BR.json';
import seo_enJson from './i18/SEO/seo_en.json';

export const getEnGBMessages = () => ({ ...enJson, ...en_GBJson, ...seo_enJson });

export const getMessagesFileByLocale = (locale: AvailableLanguage, userLocation: AcceptedCountries | undefined): Record<string, string> => {
  switch (locale) {
    case AvailableLanguage.GERMAN:
      return { ...deJson, ...seo_enJson };
    case AvailableLanguage.FINNISH:
      return { ...fiJson, ...seo_enJson };
    case AvailableLanguage.FRANCAIS:
      return { ...frJson, ...seo_enJson };
    case AvailableLanguage.HINDI:
      return { ...hiJson, ...seo_enJson };
    case AvailableLanguage.SPANISH:
      return { ...esJson, ...seo_enJson };
    case AvailableLanguage.PORTUGUESE_BRAZIL:
      return { ...pt_BRJson, ...seo_enJson };
    case AvailableLanguage.MAGYAR:
      return { ...huJson, ...seo_enJson };
    case AvailableLanguage.ENGLISH:
    default:
      if (userLocation === AcceptedCountries.GREAT_BRITAIN) {
        return getEnGBMessages();
      }
      if (userLocation === AcceptedCountries.IRELAND) {
        return { ...enJson, ...en_IEJson, ...seo_enJson };
      }
      if (userLocation === AcceptedCountries.INDIA) {
        return { ...enJson, ...en_INJson, ...seo_enJson };
      }
      if (userLocation === AcceptedCountries.NEW_ZEALAND) {
        return { ...enJson, ...en_NZJson, ...seo_enJson };
      }
      if (userLocation === AcceptedCountries.KENYA) {
        return { ...enJson, ...en_KEJson, ...seo_enJson };
      }
      if (userLocation === AcceptedCountries.CANADA) {
        return { ...enJson, ...en_CAJson, ...seo_enJson };
      }
      return { ...enJson, ...seo_enJson };
  }
};

export const getLanguageInFormatSupportedByLMT = (locale: AvailableLanguage) => {
  switch (locale) {
    case AvailableLanguage.PORTUGUESE_BRAZIL:
      return LiveMatchTrackerLanguage.BRAZILIAN;
    default:
      return locale;
  }
};

export const getAppLanguageBasedOnSocketLanguage = (socketLanguage: string) => {
  switch (socketLanguage) {
    case CountrySpecificLanguage.FINNISH.FINLAND:
      return AvailableLanguage.FINNISH;
    case CountrySpecificLanguage.MAGYAR.HUNGARY:
      return AvailableLanguage.MAGYAR;
    case CountrySpecificLanguage.HINDI.INDIAN:
      return AvailableLanguage.HINDI;
    case CountrySpecificLanguage.FRANCAIS.FRANCE:
      return AvailableLanguage.FRANCAIS;
    case CountrySpecificLanguage.PORTUGUESE.BRAZIL:
      return AvailableLanguage.PORTUGUESE_BRAZIL;
    case CountrySpecificLanguage.GERMAN.DEUTSCHLAND:
    case CountrySpecificLanguage.GERMAN.AUSTRIA:
    case CountrySpecificLanguage.GERMAN.SWITZERLAND:
      return AvailableLanguage.GERMAN;
    case CountrySpecificLanguage.SPANISH.SPAIN:
    case CountrySpecificLanguage.SPANISH.MEXICO:
    case CountrySpecificLanguage.SPANISH.COLOMBIA:
    case CountrySpecificLanguage.SPANISH.CHILE:
    case CountrySpecificLanguage.SPANISH.PERU:
    case CountrySpecificLanguage.SPANISH.ARGENTINA:
    case CountrySpecificLanguage.SPANISH.ECUADOR:
    case CountrySpecificLanguage.SPANISH.EL_SALVADOR:
    case CountrySpecificLanguage.SPANISH.PARAGUAY:
    case CountrySpecificLanguage.SPANISH.URUGUAY:
      return AvailableLanguage.SPANISH;
    case CountrySpecificLanguage.ENGLISH.GREAT_BRITAIN:
    case CountrySpecificLanguage.ENGLISH.IRELAND:
    case CountrySpecificLanguage.ENGLISH.NEW_ZEALAND:
    case CountrySpecificLanguage.ENGLISH.INDIA:
    case CountrySpecificLanguage.ENGLISH.CANADA:
    default:
      return AvailableLanguage.ENGLISH;
  }
};

export const getLanguageForSocket = () => {
  const {
    userData: { locale, userLocation },
  } = store.getState();

  switch (locale) {
    case AvailableLanguage.FINNISH:
      return CountrySpecificLanguage.FINNISH.FINLAND;
    case AvailableLanguage.PORTUGUESE_BRAZIL:
      return AvailableLanguage.PORTUGUESE_BRAZIL;
    case AvailableLanguage.MAGYAR:
      return CountrySpecificLanguage.MAGYAR.HUNGARY;
    case AvailableLanguage.HINDI:
      return CountrySpecificLanguage.HINDI.INDIAN;
    case AvailableLanguage.FRANCAIS:
      return CountrySpecificLanguage.FRANCAIS.FRANCE;
    case AvailableLanguage.GERMAN:
      switch (userLocation) {
        case AcceptedCountries.AUSTRIA:
          return CountrySpecificLanguage.GERMAN.AUSTRIA;
        case AcceptedCountries.SWITZERLAND:
          return CountrySpecificLanguage.GERMAN.SWITZERLAND;
        default:
          return CountrySpecificLanguage.GERMAN.DEUTSCHLAND;
      }
    case AvailableLanguage.SPANISH:
      switch (userLocation) {
        case AcceptedCountries.MEXICO:
          return CountrySpecificLanguage.SPANISH.MEXICO;
        case AcceptedCountries.COLOMBIA:
          return CountrySpecificLanguage.SPANISH.COLOMBIA;
        case AcceptedCountries.CHILE:
          return CountrySpecificLanguage.SPANISH.CHILE;
        case AcceptedCountries.PERU:
          return CountrySpecificLanguage.SPANISH.PERU;
        case AcceptedCountries.ARGENTINA:
          return CountrySpecificLanguage.SPANISH.ARGENTINA;
        case AcceptedCountries.ECUADOR:
          return CountrySpecificLanguage.SPANISH.ECUADOR;
        case AcceptedCountries.EL_SALVADOR:
          return CountrySpecificLanguage.SPANISH.EL_SALVADOR;
        case AcceptedCountries.PARAGUAY:
          return CountrySpecificLanguage.SPANISH.PARAGUAY;
        case AcceptedCountries.URUGUAY:
          return CountrySpecificLanguage.SPANISH.URUGUAY;
        default:
          return CountrySpecificLanguage.SPANISH.SPAIN;
      }
    case AvailableLanguage.ENGLISH:
      switch (userLocation) {
        case AcceptedCountries.GREAT_BRITAIN:
          return CountrySpecificLanguage.ENGLISH.GREAT_BRITAIN;
        case AcceptedCountries.IRELAND:
          return CountrySpecificLanguage.ENGLISH.IRELAND;
        case AcceptedCountries.NEW_ZEALAND:
          return CountrySpecificLanguage.ENGLISH.NEW_ZEALAND;
        case AcceptedCountries.INDIA:
          return CountrySpecificLanguage.ENGLISH.INDIA;
        case AcceptedCountries.CANADA:
          return CountrySpecificLanguage.ENGLISH.CANADA;
        case AcceptedCountries.KENYA:
        case AcceptedCountries.BOSNIA_AND_HERZEGOVINA:
        case AcceptedCountries.KOSOVO:
        case AcceptedCountries.SERBIA:
        case AcceptedCountries.MONTENEGRO:
        case AcceptedCountries.CROATIA:
        case AcceptedCountries.UNITED_ARAB_EMIRATES:
        case AcceptedCountries.ARMENIA:
        case AcceptedCountries.SLOVAKIA:
        case AcceptedCountries.SLOVENIA:
        case AcceptedCountries.GEORGIA:
        case AcceptedCountries.NORWAY:
        case AcceptedCountries.ISLE_OF_MAN:
        default:
          return CountrySpecificLanguage.ENGLISH.GREAT_BRITAIN;
      }
    default:
      return CountrySpecificLanguage.ENGLISH.GREAT_BRITAIN;
  }
};

export const getLanguageByCountry = (country: AcceptedCountries): AvailableLanguage => {
  switch (country) {
    case AcceptedCountries.GREAT_BRITAIN:
    case AcceptedCountries.IRELAND:
    case AcceptedCountries.NEW_ZEALAND:
      return AvailableLanguage.ENGLISH;
    case AcceptedCountries.AUSTRIA:
    case AcceptedCountries.SWITZERLAND:
      return AvailableLanguage.GERMAN;
    case AcceptedCountries.FINLAND:
      return AvailableLanguage.FINNISH;
    case AcceptedCountries.BRAZIL:
      return AvailableLanguage.PORTUGUESE_BRAZIL;
    case AcceptedCountries.HUNGARY:
      return AvailableLanguage.MAGYAR;
    case AcceptedCountries.MEXICO:
    case AcceptedCountries.COLOMBIA:
    case AcceptedCountries.CHILE:
    case AcceptedCountries.PERU:
    case AcceptedCountries.ARGENTINA:
    case AcceptedCountries.ECUADOR:
    case AcceptedCountries.EL_SALVADOR:
    case AcceptedCountries.PARAGUAY:
    case AcceptedCountries.URUGUAY:
      return AvailableLanguage.SPANISH;
    case AcceptedCountries.ISLE_OF_MAN:
    case AcceptedCountries.INDIA: // return Languages.HINDI; TODO: Uncomment when PO want to have Hindi as default language for users located in India
    case AcceptedCountries.KENYA:
    case AcceptedCountries.BOSNIA_AND_HERZEGOVINA:
    case AcceptedCountries.KOSOVO:
    case AcceptedCountries.SERBIA:
    case AcceptedCountries.MONTENEGRO:
    case AcceptedCountries.CROATIA:
    case AcceptedCountries.UNITED_ARAB_EMIRATES:
    case AcceptedCountries.ARMENIA:
    case AcceptedCountries.SLOVAKIA:
    case AcceptedCountries.SLOVENIA:
    case AcceptedCountries.GEORGIA:
    case AcceptedCountries.CANADA:
    case AcceptedCountries.NORWAY:
    default:
      return AvailableLanguage.ENGLISH;
  }
};

const hasFrontendDisabledCasino = (country: AcceptedCountries) => COUNTRIES_WITH_FRONTEND_DISABLED_CASINO.includes(country);

const canShowAviatorInMenuForCountry = (country: AcceptedCountries) => COUNTRIES_WITH_AVIATOR_GAME_IN_MENU.includes(country);

export const setGeoLocationInfo = async () => {
  try {
    const { country, canPlayCasino: isCasinoEnabledInDB, useSignupV3, loginV3Options } = await getGeoLocationInfo();
    const locale = getCookie(preferredLanguage) || getLanguageByCountry(country);
    const isCasinoEnabled = isCasinoEnabledInDB && !hasFrontendDisabledCasino(country);
    const canShowAviator = isCasinoEnabledInDB && AVIATOR_ALLOWED_IN_MENU && canShowAviatorInMenuForCountry(country);

    setLocale(locale);
    setUserLocation(country);
    setGeoLocalisation(country);
    setCanPlayCasino(isCasinoEnabled);
    setCanShowAviator(canShowAviator);
    setUseSignupV3(useSignupV3);
    if (loginV3Options && loginV3Options.length) {
      setLoginOptions(loginV3Options);
    }
  } catch (e) {
    setLocale(AvailableLanguage.ENGLISH);
    setUserLocation(AcceptedCountries.GREAT_BRITAIN);
    setGeoLocalisation(AcceptedCountries.GREAT_BRITAIN);
  }
};

export const isGBUser = () => {
  const {
    userData: { user },
  } = store.getState();

  return !!(user && user.address && user.address.country === AcceptedCountries.GREAT_BRITAIN);
};

export const getBrandTextId = () => {
  const { betkwiffDomain } = store.getState().customization.featureFlags;

  if (isProjectPhoenix()) {
    return 'project_phoenix.brand';
  }

  return betkwiffDomain ? 'common.logo.betkwiff' : 'common.logo';
};

export const isBetkwiffDomain = () => {
  if (isProductionEnv()) {
    return !KWIFF_DOMAINS.includes(window.location.host);
  }

  return isDevEnv() && !isDevKwiff() && !isLocalhost() && isDevBetkwiff();
};

export const isProductionBetkwiffKenyaDomain = () => {
  return window.location.origin.includes(PROD_KENYA_DOMAIN);
};

export const setBetkwiffFlag = () => {
  setBetkwiffDomainFeatureFlag(isBetkwiffDomain());
};

export const getBrandName = () => {
  if (isProjectPhoenix()) {
    return PROJECT_PHOENIX_BRAND_NAME;
  }

  return isBetkwiffDomain() ? BETKWIFF_BRAND_NAME : KWIFF_BRAND_NAME;
};

export const getDomainForLinks = (isPromotions = false) => {
  if (isProjectPhoenix()) {
    return PROJECT_PHOENIX_DOMAIN_ORIGIN;
  }

  // TODO: remove "isPromotions" flag when all other deeplinks will be available for betkwiff
  return isBetkwiffDomain() && isPromotions ? BETKWIFF_DOMAIN : KWIFF_DOMAIN;
};

export const getEmailToUse = () => {
  const {
    customization: {
      featureFlags: { betkwiffDomain },
    },
    userData: { userLocation },
  } = store.getState();

  if (isProjectPhoenix()) {
    return 'support-eng@customerservices365.com';
  }

  const isKenya = userLocation === AcceptedCountries.KENYA;

  return betkwiffDomain ? (isKenya ? CUSTOMER_SERVICE_EMAIL.KENYA : HELP_BETKWIFF_EMAIL) : HELP_KWIFF_EMAIL;
};

export const getLanguageShortByLanguageLong = (long: string) => {
  for (const language of availableLanguages) {
    if (language.long === long) {
      return language.short as AvailableLanguage;
    }
  }
  return AvailableLanguage.ENGLISH;
};

export const getLanguageLongByLanguageShort = (short: string) => {
  for (const language of availableLanguages) {
    if (language.short === short) {
      return language.long;
    }
  }
  return 'English';
};

export const isUserCountryEligibleForAmericanOdds = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? americanOddsWhiteListedCountries.includes(userLocation) : false;
};

export const isUserCountryEligibleForPromotionTabs = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? promotionTabsWhiteListedCountries.includes(userLocation) : false;
};

export const isUserCountryEligibleForCallUs = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? countriesWithCallUs.includes(userLocation) : false;
};

export const isUserCountryWhiteListedForBypassingReCaptcha = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? bypassReCaptchaWhiteListCountries.includes(userLocation) : false;
};

export const isFirstDepositMandatoryForUserCountry = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? signupMandatoryFirstDepositCountriesList.includes(userLocation) : false;
};

export const isUserCountryEligibleToSeeDepositAcknowledgeDialog = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? depositAcknowledgeDialogDisplayForCountries.includes(userLocation) : false;
};

export const shouldTermsAndConditionsBeAutoCheckedForUserCountry = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? termsAndConditionsAutoCheckedForCountries.includes(userLocation) : false;
};

export const hasUserCountryBetTaxes = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? countriesWithBetTaxes.includes(userLocation) : false;
};

export const isUserCountryBlockedFromSeeingWalletProfitAndLoss = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? walletProfitAndLossBlockedCountries.includes(userLocation) : false;
};

export const isUserCountryEligibleForReferAFriend = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? referAFriendEligibleCountries.includes(userLocation) : false;
};

export const isUserCountryEligibleForStayingInControl = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  if (isProjectPhoenix()) {
    return false;
  }

  return userLocation ? !hideStayingInControlForCountries.includes(userLocation) : false;
};

export const isUserCountryEligibleForResponsibleGambling = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? !hideResponsibleGamblingForCountries.includes(userLocation) : false;
};

export const isUserCountryEligibleForCloseAccount = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  if (isProjectPhoenix()) {
    return false;
  }

  return userLocation ? !hideCloseAccountForCountries.includes(userLocation) : false;
};

export const hasClosedLoopWithdrawals = (userLocation: AcceptedCountries | undefined) =>
  userLocation ? !COUNTRIES_WITHOUT_CLOSED_LOOP_WITHDRAWALS.includes(userLocation) : true;

export const getWithdrawalProcessTime = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  switch (userLocation) {
    case AcceptedCountries.KENYA:
      return 12;
    default:
      return 24;
  }
};

export const isPasswordPatternValid = (password: string) => {
  const {
    customization: { internationalSettings },
  } = store.getState();
  const { countrySettingsByLocation } = internationalSettings;

  if (countrySettingsByLocation) {
    switch (countrySettingsByLocation.passwordPattern) {
      case PASSWORD_PATTERN.FOUR_CHARS:
        return fourCharactersRegex.test(password);
      case PASSWORD_PATTERN.REGULAR:
      default:
        return passwordRegex.test(password);
    }
  }

  return passwordRegex.test(password);
};

export const getInvalidPasswordPatternMessageId = () => {
  const {
    customization: {
      internationalSettings: { countrySettingsByLocation },
    },
  } = store.getState();

  if (countrySettingsByLocation) {
    switch (countrySettingsByLocation.passwordPattern) {
      case PASSWORD_PATTERN.FOUR_CHARS:
        return 'password.message.simple_password';
      case PASSWORD_PATTERN.REGULAR:
      default:
        return 'password.message';
    }
  }

  return 'password.message';
};

export const getFullCountryNameByIsoCode = (country: string) => {
  const acceptedCountry = find(acceptedCountriesList, { code: country as AcceptedCountries });

  return acceptedCountry ? acceptedCountry.name : country;
};

export const getTermsLinkByCountry = (domain: string) => {
  const {
    userData: { userLocation },
  } = store.getState();

  if (isProjectPhoenix()) {
    return 'https://help.bet365.com/terms-and-conditions';
  }

  switch (userLocation) {
    case AcceptedCountries.GREAT_BRITAIN:
      return `https://corporate.${domain}.com/terms-conditions/`;
    case AcceptedCountries.KENYA:
      return `https://corporate.${domain}.com/terms-conditions-ke/`;
    case AcceptedCountries.BRAZIL:
      return `https://corporate.${domain}.com//terms-conditions-iom-brpt/`;
    case AcceptedCountries.NEW_ZEALAND:
      return `https://corporate.${domain}.com/terms-conditions-row/`;
    case AcceptedCountries.COLOMBIA:
    case AcceptedCountries.CHILE:
    case AcceptedCountries.PERU:
    case AcceptedCountries.ARGENTINA:
    case AcceptedCountries.ECUADOR:
    case AcceptedCountries.EL_SALVADOR:
    case AcceptedCountries.MEXICO:
    case AcceptedCountries.PARAGUAY:
    case AcceptedCountries.URUGUAY:
      return `https://corporate.${domain}.com/terms-conditions-iom-es/`;
    default:
      return `https://corporate.${domain}.com/terms-conditions-iom/`;
  }
};

export const getPrivacyPolicyLinkByCountry = (domain: string) => {
  const {
    userData: { userLocation },
  } = store.getState();

  switch (userLocation) {
    case AcceptedCountries.BRAZIL:
      return `https://corporate.${domain}.com/privacy-notice-brpt/`;
    case AcceptedCountries.COLOMBIA:
    case AcceptedCountries.CHILE:
    case AcceptedCountries.PERU:
    case AcceptedCountries.ARGENTINA:
    case AcceptedCountries.ECUADOR:
    case AcceptedCountries.EL_SALVADOR:
    case AcceptedCountries.MEXICO:
    case AcceptedCountries.PARAGUAY:
    case AcceptedCountries.URUGUAY:
      return `https://corporate.${domain}.com/privacy-notice-es/`;
    default:
      return `https://corporate.${domain}.com/privacy-notice/`;
  }
};

export const getBettingRulesLinkByCountry = (domain: string) => {
  const {
    userData: { userLocation },
  } = store.getState();

  switch (userLocation) {
    case AcceptedCountries.BRAZIL:
      return `https://corporate.${domain}.com/betting-rules-brpt/`;
    case AcceptedCountries.COLOMBIA:
    case AcceptedCountries.CHILE:
    case AcceptedCountries.PERU:
    case AcceptedCountries.ARGENTINA:
    case AcceptedCountries.ECUADOR:
    case AcceptedCountries.EL_SALVADOR:
    case AcceptedCountries.MEXICO:
    case AcceptedCountries.PARAGUAY:
    case AcceptedCountries.URUGUAY:
      return `https://corporate.${domain}.com/betting-rules-es/`;
    default:
      return `https://corporate.${domain}.com/betting-rules/`;
  }
};

export const getComplaintsLinkByCountry = (domain: string) => {
  const {
    userData: { userLocation },
  } = store.getState();

  switch (userLocation) {
    case AcceptedCountries.BRAZIL:
      return `https://corporate.${domain}.com/complaints-procedure-brpt/`;
    case AcceptedCountries.COLOMBIA:
    case AcceptedCountries.CHILE:
    case AcceptedCountries.PERU:
    case AcceptedCountries.ARGENTINA:
    case AcceptedCountries.ECUADOR:
    case AcceptedCountries.EL_SALVADOR:
    case AcceptedCountries.MEXICO:
    case AcceptedCountries.PARAGUAY:
    case AcceptedCountries.URUGUAY:
      return `https://corporate.${domain}.com/complaints-procedure-es/`;
    default:
      return `https://corporate.${domain}.com/complaints-procedure/`;
  }
};

export const getResponsibleGamblingLinkByCountry = (domain: string) => {
  const {
    userData: { userLocation },
  } = store.getState();

  switch (userLocation) {
    case AcceptedCountries.KENYA:
      return `https://corporate.${domain}.com/responsible-gambling-ke/`;
    default:
      return `https://corporate.${domain}.com/responsible-gambling/`;
  }
};

export const helpResponsibleGamblingWithDeeplinkInsteadOfSubMenu = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  switch (userLocation) {
    case AcceptedCountries.KENYA:
      return true;
    default:
      return false;
  }
};

export const shouldHideRAFButtonFromSurpriseBetDialog = () => {
  const {
    userData: { userLocation },
    customization: {
      featureFlags: { referAFriend },
    },
  } = store.getState();

  const countryBlocked = userLocation ? hideRAFButtonFromSurpriseBetCountries.includes(userLocation) : false;

  return countryBlocked || !referAFriend;
};

export const shouldHideShareButtonFromSurpriseBetDialog = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? hideShareButtonFromSurpriseBetCountries.includes(userLocation) : false;
};

export const shouldHideAccountDetailsFromProfile = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? hideAccountDetailsForCountries.includes(userLocation) : false;
};

export const getPromotionsLinkByCountry = (domain: string) => {
  const {
    userData: { userLocation },
  } = store.getState();

  switch (userLocation) {
    case AcceptedCountries.FINLAND:
      return `https://promo.betkwiff.com/betkwiff-fi-promotions/`;
    case AcceptedCountries.BRAZIL:
      return `https://promo.betkwiff.com/betkwiff-br-promotions/`;
    default:
      return `https://promo.${domain}.com`;
  }
};

export const handleJoinByCountry = () => {
  const {
    userData: { geoLocation },
  } = store.getState();

  switch (geoLocation) {
    case AcceptedCountries.FINLAND:
      setSignupChoiceDialogOpen(true);
      break;
    default:
      handleJoin();
      break;
  }
};

export const shouldDisplayHROnFeaturedPage = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? displayHROnFeaturedPageCountries.includes(userLocation) : false;
};

export const handleSettingsFetch = async () => {
  await getAndSetAllCountrySettings();
  await tryToGetWalletSettings();
};

export const getAndSetAllCountrySettings = async () => {
  try {
    const allCountrySettings = await getAllCountrySettings();
    setAllAvailableCountrySettings(allCountrySettings);
    setCountrySettingsByUserLocation();
  } catch (e) {
    // do nothing
  }
};

export const isCancelWithdrawalAvailable = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? !cancelWithdrawalBlockedCountries.includes(userLocation) : false;
};

export const showFooterPaymentLogosInUserLocation = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation && !countriesWithHiddenPaymentLogosInFooter.includes(userLocation);
};

export const showCorporateLinksInUserLocation = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation && !countriesWithoutCorporateLinksInFooter.includes(userLocation) && !isProjectPhoenix();
};

export const hideFooterBannerInUserLocation = () => {
  const {
    userData: { userLocation, authentication },
  } = store.getState();

  return userLocation && countriesWithHiddenFooterForGhostUsers.includes(userLocation) && authentication !== auth.AUTHENTICATED;
};

export const handleBrandRedirect = (from: string, to: string) => {
  const domain = window.location.origin.replace(from, to);
  const path = window.location.pathname;
  const params = window.location.search;

  window.location.replace(`${domain}${path}${params}`);
};

export const shouldHideVerificationFromProfile = () => {
  const {
    userData: { userLocation },
  } = store.getState();

  return userLocation ? hideVerificationForCountries.includes(userLocation) : false;
};

export const areSystemBetsAvailable = (userLocation: AcceptedCountries) =>
  !forceBlockSystemBetsForCountries.includes(userLocation) && COMBO_BETS_ENABLED;

export const isGBUserLocation = (userLocation: AcceptedCountries | undefined) => userLocation === AcceptedCountries.GREAT_BRITAIN;

export const isGBOrIEUserLocation = (userLocation: AcceptedCountries | undefined) =>
  userLocation ? [AcceptedCountries.GREAT_BRITAIN, AcceptedCountries.IRELAND].includes(userLocation) : false;

export const shouldOverrideLocalLanguage = () => !getCookie(preferredLanguage);
export const shouldOverrideSocketLanguage = () => !!getCookie(preferredLanguage);

export const isProjectPhoenix = () => {
  if (!PROJECT_PHOENIX_DOMAIN_ORIGIN) {
    return false;
  }

  return window.location.host.includes(PROJECT_PHOENIX_DOMAIN_ORIGIN);
};

export const isZendeskDisabled = (userLocation: AcceptedCountries | undefined) => {
  if (ZENDESK_DISABLE_IN_COUNTRIES && ZENDESK_DISABLE_IN_COUNTRIES.length && userLocation) {
    return ZENDESK_DISABLE_IN_COUNTRIES.includes(userLocation);
  }

  return false;
};

export const getIntlSuperchargedKwiffedPersonKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.supercharged_bet.kwiffed_person';
  }

  return 'supercharged_bet.kwiffed_person';
};

export const getIntlSuperchargedKwiffedPersonMinimisedKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.supercharged_bet.kwiffed_person_minimised';
  }

  return 'supercharged_bet.kwiffed_person_minimised';
};

export const getIntlTicketCashoutKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.ticket.cashout';
  }

  return 'ticket.cashout';
};

export const getIntlKwiffedTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.boosted';
  }

  return 'common.kwiffed';
};

export const getIntlKwiffedOddsTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.boosted_odds';
  }

  return 'common.kwiffed_odds';
};

export const getIntlKwiffedPayoutTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.boosted_payout';
  }

  return 'kwiffed.kwiffed_payout';
};

export const getIntlTicketKwiffedCashOutTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.ticket.kwiffed_cashout';
  }

  return 'ticket.kwiffed_cashout';
};

export const getIntlCasinoFiltersItIsKwiffmasTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.casino_filters.it_is_kwiffmas';
  }

  return 'casino_filters.it_is_kwiffmas';
};

export const getIntlCasinoFiltersEvenMoreKwiffmasSlotsTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.casino_filters.even_more_kwiffmas_slots';
  }

  return 'casino_filters.even_more_kwiffmas_slots';
};

export const getIntlCasinoFiltersKwiffmasTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.casino_filters.kwiffmas';
  }

  return 'casino_filters.kwiffmas';
};

export const getIntlCasinoFiltersHappyKwiffentinesTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.casino_filters.happy_kwiffentines';
  }

  return 'casino_filters.happy_kwiffentines';
};

export const getIntlCasinoFiltersKwiffoweenTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.casino_filters.kwiffoween';
  }

  return 'casino_filters.kwiffoween';
};

export const getIntlAppTourSuperchargeTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.app_tour.bet_supercharge';
  }

  return 'app_tour.bet_supercharge';
};

export const getIntlAppTourCashoutSuperchargeTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.app_tour.cashout_supercharge';
  }

  return 'app_tour.cashout_supercharge';
};

export const getIntlClosedLoopSummaryOneTextKey = () => {
  if (isProjectPhoenix()) {
    return 'project_phoenix.profile.wallet.withdrawal.closed_loop_summary_part_one';
  }

  return 'profile.wallet.withdrawal.closed_loop_summary_part_one';
};

export const isFeedbackForumEligible = (forumFeatureFlag: boolean) => forumFeatureFlag && !isProjectPhoenix();

export const isUserEligibleForDepositOptionsCompliance = (userLocation: AcceptedCountries | undefined) =>
  isGBOrIEUserLocation(userLocation) && !isProjectPhoenix();
